import { SearchInput } from '@components/search/SearchInput'
import {
  FilterField,
  RentRangeInput,
  TableFilterPanel
} from '@components/table/filters'
import { useLocations } from '@services/common/useLocation'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Combobox, ComboboxOption } from '@/components/ui/combobox'

const FILTER_KEYS = [
  'status',
  'min_rent',
  'max_rent',
  'onboarded',
  'location'
] as const

interface Filters {
  status?: string
  min_rent?: number
  max_rent?: number
  onboarded?: string
  location?: string
}

function parseFiltersFromParams(searchParams: URLSearchParams): Filters {
  return {
    status: searchParams.get('status') || undefined,
    min_rent: searchParams.get('min_rent')
      ? Number(searchParams.get('min_rent'))
      : undefined,
    max_rent: searchParams.get('max_rent')
      ? Number(searchParams.get('max_rent'))
      : undefined,
    onboarded: searchParams.get('onboarded') || undefined,
    location: searchParams.get('location') || undefined
  }
}

// Flatten the location tree to leaf options ("Village, District").
function flattenLocations(nodes: any[], parent = ''): ComboboxOption[] {
  const out: ComboboxOption[] = []
  for (const node of nodes ?? []) {
    const children = node?.children as any[] | undefined
    if (Array.isArray(children) && children.length > 0) {
      out.push(...flattenLocations(children, node.title))
    } else if (node?.value) {
      out.push({
        value: String(node.value),
        label: parent ? `${node.title}, ${parent}` : node.title
      })
    }
  }
  return out
}

interface Props {
  actionBar?: ReactElement
}

export function FlatsTableFilters({ actionBar }: Props) {
  const { t } = useTranslation('flat')
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: locations } = useLocations()

  const [filtersUI, setFiltersUI] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )
  const [filters, setFilters] = useState<Filters>(() =>
    parseFiltersFromParams(searchParams)
  )

  const locationOptions = useMemo(
    () => flattenLocations((locations as any[]) ?? []),
    [locations]
  )

  const areEqual = (a: Filters, b: Filters) =>
    JSON.stringify(a) === JSON.stringify(b)

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (areEqual(filtersUI, filters)) return
      setFilters(filtersUI)

      const params = new URLSearchParams(searchParams)
      FILTER_KEYS.forEach((key) => params.delete(key))
      params.delete('offset')

      if (filtersUI.status !== undefined) params.set('status', filtersUI.status)
      if (filtersUI.min_rent !== undefined)
        params.set('min_rent', String(filtersUI.min_rent))
      if (filtersUI.max_rent !== undefined)
        params.set('max_rent', String(filtersUI.max_rent))
      if (filtersUI.onboarded !== undefined)
        params.set('onboarded', filtersUI.onboarded)
      if (filtersUI.location) params.set('location', filtersUI.location)

      setSearchParams(params)
    }, 400)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersUI])

  const hasActiveFilters = useMemo(
    () =>
      filtersUI.status !== undefined ||
      filtersUI.min_rent !== undefined ||
      filtersUI.max_rent !== undefined ||
      filtersUI.onboarded !== undefined ||
      filtersUI.location !== undefined,
    [filtersUI]
  )

  const handleClear = useCallback(() => setFiltersUI({}), [])

  const numberOrUndefined = (v: string) =>
    v === '' ? undefined : Number(v.replace(/\D/g, ''))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput />
        {actionBar}
      </div>

      <TableFilterPanel
        title={t('filters.title')}
        clearLabel={t('filters.clear')}
        hasActiveFilters={hasActiveFilters}
        onClear={handleClear}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FilterField label={t('table.status')}>
            <Combobox
              searchable={false}
              clearable
              value={filtersUI.status}
              placeholder={t('filters.status')}
              onChange={(v) => setFiltersUI({ ...filtersUI, status: v })}
              options={[
                { label: t('statuses.AVAILABLE'), value: 'AVAILABLE' },
                { label: t('statuses.NOT_AVAILABLE'), value: 'NOT_AVAILABLE' },
                { label: t('statuses.RENTED'), value: 'RENTED' },
                { label: t('statuses.DRAFT'), value: 'DRAFT' }
              ]}
            />
          </FilterField>

          <FilterField label={t('table.location')}>
            <Combobox
              clearable
              value={filtersUI.location}
              placeholder={t('table.location')}
              onChange={(v) => setFiltersUI({ ...filtersUI, location: v })}
              options={locationOptions}
            />
          </FilterField>

          <FilterField label={t('filters.rentRange')}>
            <RentRangeInput
              minValue={filtersUI.min_rent}
              maxValue={filtersUI.max_rent}
              onMinChange={(raw) =>
                setFiltersUI({ ...filtersUI, min_rent: numberOrUndefined(raw) })
              }
              onMaxChange={(raw) =>
                setFiltersUI({ ...filtersUI, max_rent: numberOrUndefined(raw) })
              }
              fromPlaceholder={t('filters.from')}
              toPlaceholder={t('filters.to')}
            />
          </FilterField>

          <FilterField label={t('table.onboarded')}>
            <Combobox
              searchable={false}
              clearable
              value={filtersUI.onboarded}
              placeholder={t('filters.onboarded')}
              onChange={(v) => setFiltersUI({ ...filtersUI, onboarded: v })}
              options={[
                { label: t('table.yes'), value: 'true' },
                { label: t('table.no'), value: 'false' }
              ]}
            />
          </FilterField>
        </div>
      </TableFilterPanel>
    </div>
  )
}
